import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Trophy, Medal, Crown, Flame, Star, Users, Loader2,
  ChevronUp, Award, Target, Compass, Shield, Zap, User,
  Swords, RefreshCw, Check, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import AdaptiveChallengeCard from '../components/AdaptiveChallengeCard';
import CollectiveChallengeCard from '../components/CollectiveChallengeCard';
import { StreakFlame } from '../components/StreakFlame';
import TopMoversCard from '../components/TopMoversCard';

// Grade config
const GRADE_CONFIG = {
  recrue: { label: 'DEBUTANT', icon: User, color: '#71717A', bg: 'bg-zinc-700/30' },
  gardien: { label: 'GARDIEN', icon: Shield, color: '#3B82F6', bg: 'bg-blue-600/20' },
  elite: { label: 'ÉLITE', icon: Zap, color: '#A855F7', bg: 'bg-purple-600/20' },
  legende: { label: 'LÉGENDE EXTRA MILES', icon: Crown, color: '#FFD700', bg: 'bg-yellow-500/20' },
};

// Section config
const SECTIONS = [
  { key: 'constancy', label: 'Discipline de Fer', labelEn: 'Iron Discipline', icon: Flame, color: '#F97316', bgClass: 'border-orange-500/50' },
  { key: 'performance', label: 'Le Sommet', labelEn: 'The Summit', icon: Target, color: '#D4FF00', bgClass: 'border-primary/50' },
  { key: 'engagement', label: "L'Éclaireur", labelEn: 'The Scout', icon: Compass, color: '#3B82F6', bgClass: 'border-blue-500/50' },
  { key: 'streaks', label: 'Séries', labelEn: 'Streaks', icon: TrendingUp, color: '#EF4444', bgClass: 'border-red-500/50' },
];

const GradeBadge = ({ gradeId, size = 'sm' }) => {
  const grade = GRADE_CONFIG[gradeId] || GRADE_CONFIG.recrue;
  const Icon = grade.icon;
  const sizeClasses = size === 'lg' 
    ? 'px-3 py-1.5 text-sm gap-2' 
    : 'px-2 py-0.5 text-[10px] gap-1';
  const iconSize = size === 'lg' ? 'w-4 h-4' : 'w-3 h-3';

  return (
    <span
      data-testid={`grade-badge-${gradeId}`}
      className={`inline-flex items-center font-heading uppercase tracking-wider rounded ${grade.bg} border`}
      style={{ borderColor: grade.color, color: grade.color, ...( size === 'sm' ? {} : {}) }}
    >
      <span className={sizeClasses + ' flex items-center'}>
        <Icon className={iconSize} />
        {grade.label}
      </span>
    </span>
  );
};

const PodiumUser = ({ entry, position, language, streakMode }) => {
  const positionConfig = {
    1: { height: 'h-28', medalColor: 'text-yellow-400', bg: 'from-yellow-500/30 to-yellow-600/10', border: 'border-yellow-500/60', label: '1er' },
    2: { height: 'h-20', medalColor: 'text-gray-300', bg: 'from-gray-400/20 to-gray-500/10', border: 'border-gray-400/50', label: '2e' },
    3: { height: 'h-16', medalColor: 'text-amber-600', bg: 'from-amber-600/20 to-amber-700/10', border: 'border-amber-600/50', label: '3e' },
  };
  const cfg = positionConfig[position];
  const Icon = position === 1 ? Crown : Medal;
  const displayValue = streakMode ? (entry.streak || 0) : (entry.points || 0);
  const unitLabel = streakMode ? (displayValue === 1 ? 'sem.' : 'sem.') : 'pts';

  return (
    <div className={`flex flex-col items-center ${position === 1 ? 'order-2' : position === 2 ? 'order-1' : 'order-3'}`} data-testid={`podium-position-${position}`}>
      <div className="relative mb-2">
        <div className={`w-12 h-12 rounded-full bg-secondary flex items-center justify-center border-2 ${cfg.border} overflow-hidden`}>
          {entry.custom_avatar || entry.picture ? (
            <img src={entry.custom_avatar || entry.picture} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="font-heading text-lg text-foreground">{(entry.display_name || '?')[0].toUpperCase()}</span>
          )}
        </div>
        <div className="absolute -top-1 -right-1">
          <Icon className={`w-5 h-5 ${cfg.medalColor}`} />
        </div>
      </div>
      <p className="font-heading text-xs text-foreground text-center truncate max-w-[80px]">{entry.display_name}</p>
      {streakMode && entry.streak >= 1 && <StreakFlame streak={entry.streak} size="sm" />}
      {entry.grade_info && <GradeBadge gradeId={entry.grade_info.id} size="sm" />}
      <div className={`w-20 ${cfg.height} bg-gradient-to-t ${cfg.bg} border ${cfg.border} rounded-t-lg mt-2 flex flex-col items-center justify-end pb-2`}>
        <span className={`font-black text-lg ${cfg.medalColor}`}>{displayValue.toLocaleString()}</span>
        <span className="text-[10px] text-muted-foreground">{unitLabel}</span>
      </div>
    </div>
  );
};

const Leaderboard = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [totalParticipants, setTotalParticipants] = useState(0);
  const [activeCategory, setActiveCategory] = useState('constancy');
  const [myStats, setMyStats] = useState(null);
  const [pseudoInput, setPseudoInput] = useState('');
  const [editingPseudo, setEditingPseudo] = useState(false);
  const [savingPseudo, setSavingPseudo] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const fetchLeaderboard = useCallback(async (category) => {
    setLoading(true);
    try {
      const statsRes = await api.get('/leaderboard/my-stats');
      setMyStats(statsRes.data);
      if (statsRes.data.warrior_pseudo) {
        setPseudoInput(statsRes.data.warrior_pseudo);
      }

      if (category === 'streaks') {
        const streakRes = await api.get('/leaderboard/streaks?limit=20');
        setLeaderboard(streakRes.data.leaderboard);
        setCurrentUser(streakRes.data.current_user);
        setTotalParticipants(streakRes.data.total_participants);
      } else {
        const lbRes = await api.get(`/leaderboard?category=${category}&limit=20`);
        setLeaderboard(lbRes.data.leaderboard);
        setCurrentUser(lbRes.data.current_user);
        setTotalParticipants(lbRes.data.total_participants);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard(activeCategory);
  }, [activeCategory, fetchLeaderboard]);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
  };

  const handleSavePseudo = async () => {
    if (!pseudoInput.trim()) return;
    setSavingPseudo(true);
    try {
      await api.put('/users/warrior-pseudo', { warrior_pseudo: pseudoInput.trim() });
      toast.success(language === 'fr' ? 'Pseudo sauvegarde!' : 'Pseudo saved!');
      setEditingPseudo(false);
      fetchLeaderboard(activeCategory);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur';
      toast.error(msg);
    } finally {
      setSavingPseudo(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await api.post('/leaderboard/recalculate');
      toast.success(language === 'fr' ? 'Points recalculés!' : 'Points recalculated!');
      fetchLeaderboard(activeCategory);
    } catch {
      toast.error('Erreur');
    } finally {
      setRecalculating(false);
    }
  };

  const isCurrentUser = (userId) => userId === user?.user_id;
  const activeSection = SECTIONS.find(s => s.key === activeCategory);
  const isStreakMode = activeCategory === 'streaks';
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 pb-28">
        {/* Header */}
        <div className="text-center" data-testid="leaderboard-header">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-500/20 border-2 border-yellow-500 rounded-xl mb-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
          </div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase tracking-tight text-foreground">
            {language === 'fr' ? 'Triple Threat' : 'Triple Threat'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {language === 'fr'
              ? `${totalParticipants} athletes dans le classement`
              : `${totalParticipants} athletes in the rankings`}
          </p>
        </div>

        {/* Iter. 406.m — Top Movers podium (weekly + monthly biggest jumps) */}
        <TopMoversCard language={language} />

        {/* My Grade + Pseudo */}
        {myStats && (
          <Card className="bg-card border-border" data-testid="my-grade-card">
            <CardContent className="pt-5 pb-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                    {language === 'fr' ? 'Ton Grade' : 'Your Grade'}
                  </p>
                  <GradeBadge gradeId={myStats.grade?.id || 'recrue'} size="lg" />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRecalculate}
                  disabled={recalculating}
                  data-testid="recalculate-btn"
                  className="text-muted-foreground"
                >
                  <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Triple Pts Summary */}
              <div className="grid grid-cols-3 gap-2">
                {SECTIONS.filter(s => s.key !== 'streaks').map(sec => {
                  const pts = myStats[`${sec.key}_pts`] || 0;
                  const Icon = sec.icon;
                  return (
                    <div key={sec.key} className="text-center p-2 rounded-lg bg-secondary/50" data-testid={`my-stat-${sec.key}`}>
                      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: sec.color }} />
                      <p className="font-black text-lg text-foreground">{pts}</p>
                      <p className="text-[10px] text-muted-foreground">{sec.label}</p>
                    </div>
                  );
                })}
              </div>

              {/* Next Grade Progress */}
              {myStats.next_grade && (
                <div className="bg-secondary/30 rounded-lg p-3" data-testid="next-grade-progress">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-muted-foreground">
                      {language === 'fr' ? 'Prochain grade:' : 'Next grade:'} <span className="text-foreground font-heading">{myStats.next_grade.name_fr}</span>
                    </p>
                    <span className="text-xs font-bold text-primary">{myStats.next_grade.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${myStats.next_grade.progress}%` }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">{myStats.next_grade.needed_fr}</p>
                </div>
              )}

              {/* Warrior Pseudo */}
              <div className="flex items-center gap-2">
                <Swords className="w-4 h-4 text-muted-foreground" />
                {editingPseudo ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={pseudoInput}
                      onChange={(e) => setPseudoInput(e.target.value)}
                      placeholder="Pseudo d'athlete..."
                      className="h-8 text-sm bg-secondary"
                      maxLength={30}
                      data-testid="warrior-pseudo-input"
                    />
                    <Button size="sm" onClick={handleSavePseudo} disabled={savingPseudo || !pseudoInput.trim()} className="h-8" data-testid="save-pseudo-btn">
                      {savingPseudo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </Button>
                  </div>
                ) : (
                  <button
                    onClick={() => setEditingPseudo(true)}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="edit-pseudo-btn"
                  >
                    {myStats.warrior_pseudo || (language === 'fr' ? 'Choisis ton pseudo...' : 'Choose your pseudo...')}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2" data-testid="category-tabs">
          {SECTIONS.map(sec => {
            const Icon = sec.icon;
            const isActive = activeCategory === sec.key;
            return (
              <button
                key={sec.key}
                onClick={() => handleCategoryChange(sec.key)}
                data-testid={`tab-${sec.key}`}
                className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                  isActive
                    ? `border-opacity-100 bg-opacity-20 ${sec.bgClass}`
                    : 'border-border bg-secondary/30 opacity-60 hover:opacity-80'
                }`}
                style={isActive ? { borderColor: sec.color, backgroundColor: `${sec.color}15` } : {}}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? sec.color : undefined }} />
                <span className="text-[10px] font-heading uppercase tracking-wider">{sec.label}</span>
              </button>
            );
          })}
        </div>

        {/* Challenges */}
        <CollectiveChallengeCard compact />
        <AdaptiveChallengeCard compact />

        {/* Current user rank */}
        {currentUser && (
          <Card className="border-primary/50" style={{ background: `linear-gradient(135deg, ${activeSection?.color}15, transparent)` }} data-testid="current-user-rank">
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center border-2 border-primary">
                    <span className="text-xl font-black text-primary">#{currentUser.rank}</span>
                  </div>
                  <div>
                    <p className="font-heading text-base text-foreground">{language === 'fr' ? 'Ton Rang' : 'Your Rank'}</p>
                    <p className="text-sm text-muted-foreground">
                      {isStreakMode
                        ? `${currentUser.streak || 0} ${(currentUser.streak || 0) <= 1 ? 'semaine' : 'semaines'}`
                        : `${currentUser.points?.toLocaleString() || 0} pts`
                      }
                    </p>
                  </div>
                </div>
                {!isStreakMode && currentUser.rank > 1 && leaderboard[currentUser.rank - 2] && (
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground">
                      {language === 'fr' ? 'Pour monter' : 'To climb'}
                    </p>
                    <p className="text-sm font-bold text-primary flex items-center gap-1">
                      <ChevronUp className="w-4 h-4" />
                      {(leaderboard[currentUser.rank - 2]?.points || 0) - (currentUser.points || 0)} pts
                    </p>
                  </div>
                )}
                {isStreakMode && currentUser.discipline_index !== undefined && (
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground">
                      {language === 'fr' ? 'Indice Discipline' : 'Discipline Index'}
                    </p>
                    <p className={`text-lg font-black ${currentUser.discipline_index >= 70 ? 'text-green-400' : currentUser.discipline_index >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                      {currentUser.discipline_index}/100
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Podium (Top 3) */}
        {top3.length >= 2 && (
          <div className="flex items-end justify-center gap-3 px-4 pt-4" data-testid="podium">
            {top3[1] && <PodiumUser entry={top3[1]} position={2} language={language} streakMode={isStreakMode} />}
            {top3[0] && <PodiumUser entry={top3[0]} position={1} language={language} streakMode={isStreakMode} />}
            {top3[2] && <PodiumUser entry={top3[2]} position={3} language={language} streakMode={isStreakMode} />}
          </div>
        )}

        {/* Leaderboard List (rank 4+) */}
        {rest.length > 0 && (
          <Card className="bg-card border-border" data-testid="leaderboard-list">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-sm uppercase flex items-center gap-2 text-muted-foreground">
                <Users className="w-4 h-4" />
                {language === 'fr' ? 'Classement complet' : 'Full ranking'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {rest.map((entry) => (
                <div
                  key={entry.user_id}
                  data-testid={`leaderboard-entry-${entry.rank}`}
                  className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                    isCurrentUser(entry.user_id)
                      ? 'bg-primary/10 border-primary ring-1 ring-primary/30'
                      : 'bg-secondary/30 border-border hover:border-border/80'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-8 text-center font-black text-muted-foreground text-sm">{entry.rank}</span>
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center overflow-hidden border border-border">
                      {entry.custom_avatar || entry.picture ? (
                        <img src={entry.custom_avatar || entry.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xs font-bold">{(entry.display_name || '?')[0].toUpperCase()}</span>
                      )}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isCurrentUser(entry.user_id) ? 'text-primary' : 'text-foreground'}`}>
                        {entry.display_name}
                        {isCurrentUser(entry.user_id) && (
                          <span className="ml-2 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded font-heading">
                            {language === 'fr' ? 'VOUS' : 'YOU'}
                          </span>
                        )}
                      </p>
                      <div className="flex items-center gap-1">
                        {entry.grade_info && <GradeBadge gradeId={entry.grade_info.id} size="sm" />}
                        {isStreakMode && entry.streak >= 1 && <StreakFlame streak={entry.streak} size="xs" />}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {isStreakMode ? (
                      <>
                        <p className="font-black text-base text-foreground">{entry.streak || 0}</p>
                        <p className="text-[10px] text-muted-foreground">sem.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-black text-base text-foreground">{(entry.points || 0).toLocaleString()}</p>
                        <p className="text-[10px] text-muted-foreground">pts</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {leaderboard.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-leaderboard">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{language === 'fr' ? 'Aucun athlete dans ce classement' : 'No athletes in this ranking yet'}</p>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 text-[10px] text-muted-foreground px-2">
          {Object.entries(GRADE_CONFIG).map(([key, grade]) => {
            const Icon = grade.icon;
            const shortLabel = key === 'legende' ? 'LÉGENDE' : grade.label;
            return (
              <span key={key} className="flex items-center gap-1 whitespace-nowrap">
                <Icon className="w-3 h-3 flex-shrink-0" style={{ color: grade.color }} />
                {shortLabel}
              </span>
            );
          })}
        </div>
      </div>
    </Layout>
  );
};

export default Leaderboard;
